import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ScanLineIcon, ShieldAlertIcon, ShieldCheckIcon, WarehouseIcon, UploadIcon, FileSpreadsheetIcon, PackagePlusIcon, Trash2Icon, CheckCircle2Icon, FileIcon, EditIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import ContainerEditModal from "@/components/ContainerEditModal";
import { useAuth } from "@/context/AuthContext";

export default function Triagem() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "manager";
  const [containers, setContainers] = useState([]);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState({ no_serie: "", nf_contentor: "", tara_aferida: "" });
  const [scanned, setScanned] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [manualOpen, setManualOpen] = useState(false);
  const [manualForm, setManualForm] = useState({ no_serie: "", tipo_armazenamento: "TANQUE", capacidade: 5000, tara: 850, nf_contentor: "", observacao: "" });
  const fileRef = React.useRef(null);

  const load = async () => {
    const { data } = await api.get("/containers");
    setContainers(data);
  };
  useEffect(() => { load(); }, []);

  const onScan = async (no) => {
    setForm((f) => ({ ...f, no_serie: no }));
    if (!no) return setScanned(null);
    try {
      const { data } = await api.get(`/containers/${no}`);
      setScanned(data);
    } catch { setScanned(null); }
  };

  const todayIso = new Date().toISOString().slice(0, 10);
  const isPastOrToday = (d) => d && d <= todayIso;
  const within30 = (d) => {
    if (!d) return false;
    const dt = new Date(d);
    return (dt - new Date()) / 86400000 <= 30 && (dt - new Date()) / 86400000 > 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/containers/triagem", {
        no_serie: form.no_serie,
        nf_contentor: form.nf_contentor,
        tara_aferida: parseFloat(form.tara_aferida),
      });
      if (data.status === "EM_MANUTENCAO") toast.error(`Bloqueado: ${data.motivo}`);
      else toast.success("Contentor aprovado e adicionado à fila");
      setForm({ no_serie: "", nf_contentor: "", tara_aferida: "" });
      setScanned(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const [pendingFile, setPendingFile] = useState(null); // File pré-upload
  const [uploading, setUploading] = useState(false);

  const uploadBackload = async () => {
    if (!pendingFile) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", pendingFile);
    try {
      const { data } = await api.post("/triagem/upload-backload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Backload importado: ${data.inserted} novos + ${data.updated} atualizados`);
      if (data.errors?.length) toast.warning(`${data.errors.length} linhas com erro — ver console do backend`);
      setPendingFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const selectFile = (file) => {
    if (!file) return;
    const okExt = /\.(xlsx|xls|csv)$/i.test(file.name);
    if (!okExt) return toast.error("Arquivo inválido — use .xlsx, .xls ou .csv");
    if (file.size > 10 * 1024 * 1024) return toast.error("Arquivo grande demais (limite 10MB)");
    setPendingFile(file);
  };

  const clearPendingFile = () => {
    setPendingFile(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const humanSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  };

  const filtered = (list) => list.filter((c) => !searchTerm || c.no_serie.toLowerCase().includes(searchTerm.toLowerCase()));

  const aguardandoInspecao = useMemo(() => filtered(containers.filter((c) => c.status === "AGUARDANDO_INSPECAO")), [containers, searchTerm]);
  const aguardandoManut = useMemo(() => filtered(containers.filter((c) => c.status === "EM_MANUTENCAO")), [containers, searchTerm]);
  const vaziosAptos = useMemo(() => filtered(containers.filter((c) => c.status === "VAZIO_PRONTO")), [containers, searchTerm]);
  const cheios = useMemo(() => filtered(containers.filter((c) => c.status === "CHEIO" || c.status === "EM_TRANSITO")), [containers, searchTerm]);

  const inspecionar = async (no_serie, aprovado) => {
    let motivo = null;
    if (!aprovado) {
      motivo = window.prompt("Motivo da reprovação (ex: válvula danificada, tara divergente, resíduo químico...):");
      if (!motivo || !motivo.trim()) return toast.error("Motivo obrigatório para reprovação");
    }
    try {
      const { data } = await api.post(`/containers/${no_serie}/inspecionar`, { aprovado, motivo });
      toast[aprovado ? "success" : "warning"](aprovado ? `${no_serie} aprovado → Vazio Apto` : `${no_serie} reprovado → Manutenção`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submitManual = async () => {
    if (!manualForm.no_serie.trim()) return toast.error("Informe o N° Série");
    try {
      await api.post("/containers/vazio-manual", {
        ...manualForm,
        no_serie: manualForm.no_serie.trim().toUpperCase(),
        capacidade: parseInt(manualForm.capacidade, 10),
        tara: parseFloat(manualForm.tara),
      });
      toast.success(`${manualForm.no_serie.toUpperCase()} registrado → Aguardando Inspeção`);
      setManualForm({ no_serie: "", tipo_armazenamento: "TANQUE", capacidade: 5000, tara: 850, nf_contentor: "", observacao: "" });
      setManualOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-4 md:p-8 max-w-[1700px] mx-auto">
      <header className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#00E5FF] mb-1">Estoque · Vazios</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold leading-none">Contentores Vazios</h1>
          <p className="text-xs text-muted-foreground mt-2">Recebimento manual ou por planilha · entra em AGUARDANDO INSPEÇÃO · aprovado ↦ VAZIO APTO · reprovado ↦ MANUTENÇÃO.</p>
        </div>
        <WarehouseIcon className="w-12 h-12 text-[#34C759]" strokeWidth={1.2} />
      </header>

      {/* Scanner + Backload */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-6">
        <Card className="lg:col-span-3 bg-[#14151A] border-border rounded-none p-6">
          <div className="flex items-center gap-2 mb-4">
            <ScanLineIcon className="w-5 h-5 text-[#FFD60A]" />
            <h2 className="font-display text-2xl font-bold">Bipagem de triagem (FEFO)</h2>
          </div>
          <form onSubmit={submit} className="space-y-3">
            <Input data-testid="triagem-serie" value={form.no_serie} onChange={(e) => onScan(e.target.value.toUpperCase())} placeholder="Bipa (ex: TQ-5000-1001)" required className="rounded-none bg-[#0A0A0C] font-mono h-12 border-l-4 border-l-[#FFD60A]" />
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="triagem-nf" value={form.nf_contentor} onChange={(e) => setForm({ ...form, nf_contentor: e.target.value })} placeholder="NF Contentor" required className="rounded-none bg-[#0A0A0C] font-mono h-11" />
              <Input data-testid="triagem-tara" type="number" step="0.01" value={form.tara_aferida} onChange={(e) => setForm({ ...form, tara_aferida: e.target.value })} placeholder="Tara aferida" required className="rounded-none bg-[#0A0A0C] font-mono h-11" />
            </div>
            <Button type="submit" disabled={loading || !scanned} data-testid="triagem-submit" className="w-full h-12 rounded-none bg-[#FFD60A] text-black hover:bg-[#ffdf3a] font-bold uppercase tracking-wider">
              Validar & Triar
            </Button>
          </form>
        </Card>

        <Card className="lg:col-span-2 bg-[#0A0A0C] border-border border-l-2 border-l-[#FF9F0A] rounded-none p-6">
          <div className="flex items-center gap-2 mb-3">
            <UploadIcon className="w-5 h-5 text-[#FF9F0A]" />
            <h3 className="font-display text-xl font-bold">Importar Backload (Excel)</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            Colunas aceitas: <span className="font-mono">Contentor, NF Backload, Código, Descrição</span>. Os ativos importados entram em <span className="text-[#00E5FF]">AGUARDANDO INSPEÇÃO</span> e devem ser aprovados/reprovados nesta tela.
          </p>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={(e) => selectFile(e.target.files[0])}
            className="hidden"
            data-testid="backload-input"
          />

          {/* Estado A: sem arquivo selecionado — mostra 2 botões (Planilha | Manual) */}
          {!pendingFile && (
            <div className="grid grid-cols-2 gap-2">
              <Button onClick={() => fileRef.current?.click()} data-testid="backload-upload" className="h-12 rounded-none bg-[#FF9F0A] text-black hover:bg-[#FF9F0A]/85 font-bold uppercase tracking-wider text-xs">
                <FileSpreadsheetIcon className="w-4 h-4 mr-1" /> Planilha
              </Button>
              <Button onClick={() => setManualOpen(true)} data-testid="vazio-manual-open" className="h-12 rounded-none bg-[#00E5FF] text-black hover:bg-[#00E5FF]/85 font-bold uppercase tracking-wider text-xs">
                <PackagePlusIcon className="w-4 h-4 mr-1" /> Manual
              </Button>
            </div>
          )}

          {/* Estado B: arquivo selecionado — card com nome + excluir + confirmar */}
          {pendingFile && (
            <div className="space-y-2" data-testid="backload-preview">
              <div className="flex items-center gap-3 bg-[#0A0A0C] border-2 border-dashed border-[#00E5FF]/50 px-3 py-3">
                <div className="w-10 h-10 bg-[#00E5FF]/15 flex items-center justify-center border border-[#00E5FF]/40 shrink-0">
                  <FileIcon className="w-5 h-5 text-[#00E5FF]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">Arquivo selecionado</div>
                  <div className="text-sm font-bold text-white truncate" data-testid="backload-filename">{pendingFile.name}</div>
                  <div className="text-[10px] font-mono text-[#FFD60A]" data-testid="backload-filesize">{humanSize(pendingFile.size)}</div>
                </div>
                <button
                  onClick={clearPendingFile}
                  disabled={uploading}
                  data-testid="backload-clear"
                  title="Remover arquivo e escolher outro"
                  className="w-9 h-9 bg-transparent border border-[#FF3B30]/50 text-[#FF3B30] hover:bg-[#FF3B30]/15 flex items-center justify-center disabled:opacity-40"
                >
                  <Trash2Icon className="w-4 h-4" />
                </button>
              </div>
              <Button
                onClick={uploadBackload}
                disabled={uploading}
                data-testid="backload-confirm"
                className="w-full h-12 rounded-none bg-[#34C759] text-black hover:bg-[#34C759]/85 font-bold uppercase tracking-wider text-xs disabled:opacity-60"
              >
                <CheckCircle2Icon className="w-4 h-4 mr-2" />
                {uploading ? "Processando planilha..." : "Confirmar Importação"}
              </Button>
            </div>
          )}
        </Card>
      </div>

      {/* Tabs */}
      <div className="mb-3">
        <Input
          data-testid="triagem-search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value.toUpperCase())}
          placeholder="Filtrar nas abas por N° Série..."
          className="rounded-none bg-[#14151A] font-mono h-10 max-w-md"
        />
      </div>

      <Tabs defaultValue="inspecao" className="w-full">
        <TabsList className="bg-[#14151A] rounded-none border border-border h-12 p-0">
          <TabsTrigger value="inspecao" data-testid="tab-inspecao" className="rounded-none data-[state=active]:bg-[#00E5FF] data-[state=active]:text-black h-full px-6 font-bold uppercase tracking-wider text-xs">
            Aguardando Inspeção · {aguardandoInspecao.length}
          </TabsTrigger>
          <TabsTrigger value="manutencao" data-testid="tab-manutencao" className="rounded-none data-[state=active]:bg-[#FF9F0A] data-[state=active]:text-black h-full px-6 font-bold uppercase tracking-wider text-xs">
            Em Manutenção · {aguardandoManut.length}
          </TabsTrigger>
          <TabsTrigger value="aptos" data-testid="tab-aptos" className="rounded-none data-[state=active]:bg-[#34C759] data-[state=active]:text-black h-full px-6 font-bold uppercase tracking-wider text-xs">
            Vazios Aptos · {vaziosAptos.length}
          </TabsTrigger>
          <TabsTrigger value="cheios" data-testid="tab-cheios" className="rounded-none data-[state=active]:bg-[#FFD60A] data-[state=active]:text-black h-full px-6 font-bold uppercase tracking-wider text-xs">
            Histórico de Cheios · {cheios.length}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inspecao" className="mt-4">
          <ContainerTable list={aguardandoInspecao} variant="inspecao" onInspect={inspecionar} canEdit={canEdit} onEdit={setEditTarget} />
        </TabsContent>
        <TabsContent value="manutencao" className="mt-4">
          <ContainerTable list={aguardandoManut} variant="manut" canEdit={canEdit} onEdit={setEditTarget} />
        </TabsContent>
        <TabsContent value="aptos" className="mt-4">
          <ContainerTable list={vaziosAptos} variant="aptos" canEdit={canEdit} onEdit={setEditTarget} />
        </TabsContent>
        <TabsContent value="cheios" className="mt-4">
          <ContainerTable list={cheios} variant="cheios" canEdit={canEdit} onEdit={setEditTarget} />
        </TabsContent>
      </Tabs>

      {/* Registro Manual Vazio */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="bg-[#14151A] border-border rounded-none max-w-lg" data-testid="vazio-manual-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Registrar Vazio Manualmente</DialogTitle>
            <div className="text-xs text-muted-foreground mt-1">Registro rápido de contentor vazio · entra em AGUARDANDO INSPEÇÃO.</div>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">N° Série *</Label>
              <Input data-testid="vm-serie" value={manualForm.no_serie} onChange={(e) => setManualForm({ ...manualForm, no_serie: e.target.value.toUpperCase() })} className="mt-1 rounded-none bg-[#0A0A0C] font-mono h-10" placeholder="TQ-5000-1234" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Tipo</Label>
                <select value={manualForm.tipo_armazenamento} onChange={(e) => setManualForm({ ...manualForm, tipo_armazenamento: e.target.value })} data-testid="vm-tipo" className="mt-1 w-full rounded-none bg-[#0A0A0C] border border-border h-10 px-2 text-sm">
                  <option value="TANQUE">TANQUE</option>
                  <option value="REFIL">REFIL</option>
                </select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Capac. (L)</Label>
                <Input data-testid="vm-capacidade" type="number" value={manualForm.capacidade} onChange={(e) => setManualForm({ ...manualForm, capacidade: e.target.value })} className="mt-1 rounded-none bg-[#0A0A0C] font-mono h-10" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Tara (kg)</Label>
                <Input data-testid="vm-tara" type="number" step="0.01" value={manualForm.tara} onChange={(e) => setManualForm({ ...manualForm, tara: e.target.value })} className="mt-1 rounded-none bg-[#0A0A0C] font-mono h-10" />
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">NF de retorno (opcional)</Label>
              <Input data-testid="vm-nf" value={manualForm.nf_contentor} onChange={(e) => setManualForm({ ...manualForm, nf_contentor: e.target.value })} className="mt-1 rounded-none bg-[#0A0A0C] font-mono h-10" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Observação (opcional)</Label>
              <Input data-testid="vm-obs" value={manualForm.observacao} onChange={(e) => setManualForm({ ...manualForm, observacao: e.target.value })} className="mt-1 rounded-none bg-[#0A0A0C] h-10" placeholder="Ex: contentor novo comprado" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualOpen(false)} className="rounded-none border-border">Cancelar</Button>
            <Button onClick={submitManual} data-testid="vm-submit" className="rounded-none bg-[#00E5FF] text-black hover:bg-[#00E5FF]/85 font-bold uppercase tracking-wider">Registrar Vazio</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ContainerEditModal
        open={!!editTarget}
        container={editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={() => load()}
      />
    </div>
  );
}

function ContainerTable({ list, variant, onInspect, canEdit, onEdit }) {
  const todayIso = new Date().toISOString().slice(0, 10);
  const isPast = (d) => d && d <= todayIso;
  return (
    <Card className="bg-[#14151A] border-border rounded-none p-0 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0A0C] text-[10px] uppercase tracking-widest text-muted-foreground">
            <tr>
              {variant === "aptos" && <th className="px-3 py-3 w-8"></th>}
              <th className="text-left px-3 py-3">N° Série</th>
              <th className="text-left px-3 py-3">Tipo</th>
              <th className="text-right px-3 py-3">Cap.</th>
              <th className="text-right px-3 py-3">Tara</th>
              {variant !== "cheios" && (<>
                <th className="text-left px-3 py-3">Teste Carga</th>
                <th className="text-left px-3 py-3">Hidrostático</th>
                <th className="text-left px-3 py-3">Estanqueidade</th>
              </>)}
              {variant === "cheios" && (<>
                <th className="text-left px-3 py-3">Produto</th>
                <th className="text-left px-3 py-3">Lote</th>
                <th className="text-right px-3 py-3">Qtd</th>
                <th className="text-left px-3 py-3">Local</th>
              </>)}
              <th className="text-left px-3 py-3">Status</th>
              <th className="text-right px-3 py-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr><td colSpan="11" className="text-center text-muted-foreground py-8">Vazio.</td></tr>
            )}
            {list.map((c) => (
              <tr key={c.no_serie} className="border-t border-border/60 hover:bg-[#1C1D24]" data-testid={`tri-row-${c.no_serie}`}>
                {variant === "aptos" && <td className="px-3 py-2"><input type="checkbox" data-testid={`apto-${c.no_serie}`} className="accent-[#34C759]" /></td>}
                <td className="px-3 py-2 font-mono text-[#FFD60A] font-bold">{c.no_serie}</td>
                <td className="px-3 py-2">{c.tipo_armazenamento}</td>
                <td className="px-3 py-2 text-right font-mono">{c.capacidade}L</td>
                <td className="px-3 py-2 text-right font-mono">{c.tara}</td>
                {variant !== "cheios" && (<>
                  <td className={`px-3 py-2 font-mono text-xs ${isPast(c.data_teste_carga) ? "text-[#FF3B30]" : "text-muted-foreground"}`}>{c.data_teste_carga}</td>
                  <td className={`px-3 py-2 font-mono text-xs ${isPast(c.data_hidrostatico) ? "text-[#FF3B30]" : "text-muted-foreground"}`}>{c.data_hidrostatico}</td>
                  <td className={`px-3 py-2 font-mono text-xs ${isPast(c.data_estanqueidade) ? "text-[#FF3B30]" : "text-muted-foreground"}`}>{c.data_estanqueidade}</td>
                </>)}
                {variant === "cheios" && (<>
                  <td className="px-3 py-2 text-xs">{c.nome_produto || "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs">{c.no_lote || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{c.qtd_envasado || "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{c.local_estoque || "—"}</td>
                </>)}
                <td className="px-3 py-2">
                  <Badge className="rounded-none border bg-transparent border-border text-[10px]">{c.status.replace(/_/g, " ")}</Badge>
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {variant === "inspecao" && (
                    <>
                      <Button size="sm" onClick={() => onInspect(c.no_serie, true)} data-testid={`insp-aprovar-${c.no_serie}`} className="rounded-none bg-[#34C759] text-black hover:bg-[#34C759]/85 h-8 mr-2 font-bold uppercase tracking-wider text-[10px]">
                        Aprovar
                      </Button>
                      <Button size="sm" onClick={() => onInspect(c.no_serie, false)} data-testid={`insp-reprovar-${c.no_serie}`} variant="outline" className="rounded-none border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 h-8 mr-2 font-bold uppercase tracking-wider text-[10px]">
                        Reprovar
                      </Button>
                    </>
                  )}
                  {canEdit && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onEdit(c)}
                      data-testid={`tri-editar-${c.no_serie}`}
                      className="h-8 w-8 p-0 hover:bg-[#FFD60A]/20"
                      title="Editar contentor"
                    >
                      <EditIcon className="w-3.5 h-3.5 text-[#FFD60A]" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
